import { createTheme } from '@mui/material/styles';

// Visual language of the sales dashboard: a quiet, dense, data-first look. Neutral page,
// white surfaces with hairline borders (no shadows), one accent colour, small type with
// tabular numbers, and soft tinted tags instead of saturated chips.
export const tones = {
  neutral: { bg: '#F1F3F6', fg: '#4A5568', dot: '#8A94A6' },
  primary: { bg: '#EAF0FF', fg: '#2F54D6', dot: '#3B63F0' },
  info: { bg: '#E7F3FE', fg: '#1763B8', dot: '#2B8AE8' },
  success: { bg: '#E6F6EC', fg: '#18733A', dot: '#22A455' },
  warning: { bg: '#FFF3E0', fg: '#A85A08', dot: '#F08C1A' },
  error: { bg: '#FDEBEC', fg: '#C02A31', dot: '#E5484D' },
  purple: { bg: '#F1ECFE', fg: '#6539CF', dot: '#8257E6' },
  dark: { bg: '#23262F', fg: '#FFFFFF', dot: '#FFFFFF' },
};

const border = '#E4E7EC';
const fontFamily = '"Inter", "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const salesTheme = createTheme({
  palette: {
    primary: { main: '#3558E0', dark: '#2A47BC', light: '#6C87F0', contrastText: '#fff' },
    secondary: { main: '#7048D8' },
    success: { main: '#1D8A45' },
    warning: { main: '#D9770B' },
    error: { main: '#D3343A' },
    info: { main: '#1F74D4' },
    text: { primary: '#171A23', secondary: '#667085', disabled: '#A4ACB9' },
    divider: border,
    background: { default: '#F5F6F8', paper: '#FFFFFF' },
    action: { hover: 'rgba(53, 88, 224, 0.045)', selected: 'rgba(53, 88, 224, 0.08)' },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily,
    fontSize: 13,
    h5: { fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.01em' },
    h6: { fontWeight: 700, fontSize: '1.0625rem', letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600, fontSize: '0.9375rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem', lineHeight: 1.45 },
    button: { fontWeight: 600, textTransform: 'none', fontSize: '0.8125rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { fontFeatureSettings: '"tnum", "cv11"', backgroundColor: '#F5F6F8' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: border },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, textTransform: 'none', fontWeight: 600, minHeight: 34 },
        sizeSmall: { minHeight: 30, paddingInline: 10 },
        sizeLarge: { minHeight: 44, fontSize: '0.9375rem' },
        outlined: { borderColor: border, color: '#344054', backgroundColor: '#fff', '&:hover': { borderColor: '#C9CED8', backgroundColor: '#F8F9FB' } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
        outlined: { borderColor: border },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C9CED8' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5 },
        },
        input: { fontSize: '0.8125rem' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: '0.8125rem' } } },
    MuiMenuItem: { styleOverrides: { root: { fontSize: '0.8125rem', minHeight: 34 } } },
    MuiFormControlLabel: { styleOverrides: { label: { fontSize: '0.8125rem', color: '#171A23' } } },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42 },
        indicator: { height: 2.5, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', minHeight: 42, minWidth: 'auto',
          paddingInline: 14, color: '#667085', letterSpacing: 0,
          '&.Mui-selected': { fontWeight: 600 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: '#EEF0F3', fontSize: '0.8125rem', padding: '9px 12px' },
        head: {
          fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
          color: '#667085', backgroundColor: '#FAFBFC', padding: '8px 12px', whiteSpace: 'nowrap',
          borderBottomColor: border,
        },
        stickyHeader: { backgroundColor: '#FAFBFC' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: '#F7F9FF' },
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: '#EEF3FF' },
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: { tooltip: { fontSize: '0.72rem', backgroundColor: '#23262F', borderRadius: 6 }, arrow: { color: '#23262F' } },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 14 } } },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: '1rem', fontWeight: 700 } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '12px 20px' } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', borderColor: border, color: '#667085', paddingBlock: 5,
          '&.Mui-selected': { backgroundColor: '#EAF0FF', color: '#2F54D6', '&:hover': { backgroundColor: '#DFE8FF' } },
        },
      },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 10, fontSize: '0.8125rem' } } },
    MuiAccordionSummary: { styleOverrides: { content: { alignItems: 'center' } } },
    MuiPaginationItem: { styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } } },
    MuiCheckbox: { styleOverrides: { root: { color: '#C3C9D4', padding: 6 } } },
  },
});

export default salesTheme;
